import React from 'react';
import { IConfigs } from 'src/shef-global-state/shefGlobalState';
import { useFeatureFlag } from './hooks';

interface IFeatureFlagProps {
  /** Name of feature flag to check. If enabled, by default it will render the given children or `treatment` prop. */
  name: keyof IConfigs;
  /** Renders contents if feature flag is not enabled. (Control) */
  control?: React.ReactNode | string | number;
  /** Renders contents if feature flag is enabled. (Treatment) */
  treatment?: React.ReactNode | string | number;
}

/**
 * Displays the given contents if a feature flag is enabled.
 *
 * QUICK SET UP
 * 1. Add a value to FeatureName enum.
 *    In `shef-mono/shef-api/src/feature-flags/FeatureFlag.graphql`.
 *    Spell in constant case (all-caps and underscore spacing).
 *      e.g. FEATURE_FLAG_NAME
 *
 * 2. Define a default value for your feature flag.
 *    In `shef-mono/shef-api/src/feature-flags/ConfigDefaultValues.ts` > in `ConfigDefaultValues`
 *    Name is automatically generated in camel case from step 1 (FEATURE_FLAG_NAME = FeatureFlagName).
 *      e.g. [FeatureName.FeatureFlagName]: someValue (most likely, false)
 *
 * 3. Add featureFlag to global state.
 *    In `shef-mono/shef-web/src/shef-global-state/useFetchGlobalState.ts` > in `configs` within the globalReduce hook
 *      e.g. featureFlagName: data.FeatureFlagName,
 *    Note the key name -- this is the string you will reference when using this component.
 *      e.g. in the case above, the first letter is no longer uppercase
 *
 * 4. Define a default value for featureFlag in global state.
 *    In `shef-mono/shef-web/src/shef-global-state/shefGlobalState.ts`
 *      > Declare your feature flag in `IConfigs`
 *      > Define your feature flag in `initialGlobalFetchState`
 *      e.g. { ..., configs: { ..., [featureFlagName]: someValue } } (most likely, false)
 *
 * 5. Add featureFlag to global state gql.
 *    In `shef-mono/shef-web/src/shef-global-state/useFetchGlobalState.gql`
 *    Setting the key as the same in step 3 and the name in the `isFeatureOn` function as in step 1.
 *      e.g. featureFlagName: isFeatureOn(name: FEATURE_FLAG_NAME)
 */

export const FeatureFlag: React.FC<React.PropsWithChildren<IFeatureFlagProps>> = (props) => {
  const { children, control, name, treatment } = props;
  const featureFlagValue = useFeatureFlag(name);

  if (featureFlagValue) {
    return (
      <>
        {treatment}
        {children}
      </>
    );
  }
  return <>{control}</>;
};
